import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';

// Image compression settings
const MAX_SIZE_MB = 2;
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1080;
const QUALITY = 0.8;

// Compress image before upload
const compressImage = (file, maxWidth = MAX_WIDTH, maxHeight = MAX_HEIGHT, quality = QUALITY) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
  });
};

// Generate unique filename
const generateFilename = (originalName) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop() || 'jpg';
  return `${timestamp}-${random}.${extension}`;
};

// Upload single image
export const uploadImage = async (file, path) => {
  try {
    // Compress image
    const compressedFile = await compressImage(file);
    
    // Create storage reference
    const filename = generateFilename(file.name);
    const fullPath = `${path}/${filename}`;
    const storageRef = ref(storage, fullPath);
    
    // Upload file
    await uploadBytes(storageRef, compressedFile);
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return { url: downloadURL, path: fullPath, error: null };
  } catch (error) {
    console.error('Error uploading image:', error);
    return { url: null, path: null, error: error.message };
  }
};

// Upload company logo
export const uploadCompanyLogo = async (file, companyId) => {
  const path = `companies/${companyId}`;
  return await uploadImage(file, path);
};

// Upload car images (max 5)
export const uploadCarImages = async (files, companyId, carId) => {
  try {
    const urls = [];
    const maxFiles = Math.min(files.length, 5);
    
    for (let i = 0; i < maxFiles; i++) {
      const path = `cars/${companyId}/${carId}`;
      const result = await uploadImage(files[i], path);
      
      if (result.error) {
        console.error(`Error uploading image ${i + 1}:`, result.error);
        continue;
      }
      
      urls.push(result.url);
    }
    
    return { urls, error: null };
  } catch (error) {
    console.error('Error uploading car images:', error);
    return { urls: [], error: error.message };
  }
};

// Upload user profile photo
export const uploadProfilePhoto = async (file, userId) => {
  const path = `users/${userId}`;
  return await uploadImage(file, path);
};

// Upload user documents (license, Aadhar)
export const uploadUserDocument = async (file, userId, documentType) => {
  // documentType: 'license-front', 'license-back', 'aadhar-front'
  const path = `users/${userId}/documents/${documentType}`;
  return await uploadImage(file, path);
};

// Upload booking image (before ride or after ride)
export const uploadBookingImage = async (file, bookingId, imageType) => {
  // imageType: 'before-ride' | 'after-ride'
  const path = `bookings/${bookingId}/images/${imageType}`;
  return await uploadImage(file, path);
};

// Delete image from storage
export const deleteImage = async (imagePath) => {
  try {
    const storageRef = ref(storage, imagePath);
    await deleteObject(storageRef);
    return { error: null };
  } catch (error) {
    console.error('Error deleting image:', error);
    return { error: error.message };
  }
};

// Delete multiple images
export const deleteImages = async (imagePaths) => {
  try {
    const deletePromises = imagePaths.map(path => deleteImage(path));
    await Promise.all(deletePromises);
    return { error: null };
  } catch (error) {
    console.error('Error deleting images:', error);
    return { error: error.message };
  }
};

// Get image URL from path
export const getImageUrl = async (path) => {
  try {
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    return { url, error: null };
  } catch (error) {
    console.error('Error getting image URL:', error);
    return { url: null, error: error.message };
  }
};
